import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { FormFields } from '~/components/FormFields';
import { GlobalEl } from '~/styles/app';
import notify from '~/components/Notify';
import Drawer from '~/components/Drawer';
import { useAuth } from '~/routes/components/AuthProvider';
import { Checkbox, Tab } from 'semantic-ui-react';
import { StyledTabPane, StyledMenuItem, DrawerFooter } from '../../style';
import { FeedProductService } from '~/api/services/feedProduct';
import { handleErrors } from '~/utils/helpers';
import ClimaProgramQuery from '~/QueryHooks/ClimaProgramQuery';

interface Props {
  isVisible: boolean;
  handleHiddenDrawer: () => void;
  fetchData: () => void;
  idFeedProduct?: number | null;
}

const CreateEditFeedProduct: React.FC<Props> = (props) => {
  const { isVisible, handleHiddenDrawer, idFeedProduct, fetchData } = props;
  const { t } = useTranslation();
  const { systemData: { languages } } = useAuth();
  const { handleSubmit, control, reset, formState, setValue, watch } = useForm({
    mode: 'onSubmit'
  });

  const watchStatus = watch('status')

  const [isLoading, setIsLoading] = useState(false);

  const {
    data: optionsData,
    refetch: fetchSuppliers,
    isLoading: isLoadingSuppliers
  } = ClimaProgramQuery.getFeedProductOptionsData();

  const feedSupplierOptions = optionsData?.feedSuppliers
    ? optionsData.feedSuppliers.map(item => ({
      value: item.id,
      text: item.name
    }))
    : [];

  useEffect(() => {
    if (isVisible) {
      fetchSuppliers()
      if (idFeedProduct) {
        editFeedProduct(idFeedProduct);
      } else {
        reset({
          status: 1,
          feedSupplierId: null,
          agolineContent: '',
          languages: getLangs()
        });
      }
    }
  }, [isVisible, idFeedProduct]);

  function getLangs() {
    return languages.map(language => ({
      id: language.id,
      name: ''
    }));
  }

  function getParsedLanguages(dataLangs) {
    const result = [...dataLangs];
    languages.forEach((language) => {
      const index = result.findIndex(lang => lang.id === language.id);
      if (index < 0) {
        result.push({
          id: language.id,
          name: '',
          order: language.order
        });
      } else {
        result[index].order = language.order;
      }
    });
    result.sort((a, b) => a.order - b.order);
    result.forEach(lang => delete lang.order);
    return result;
  }

  async function editFeedProduct(id: number) {
    setIsLoading(true);
    try {
      const response = await FeedProductService.edit(id);
      response.data.languages = getParsedLanguages(response.data.languages);
      reset(response.data);
    } catch (error) {
      handleErrors(error);
    }
    setIsLoading(false);
  }

  async function onSubmit(data) {
    setIsLoading(true);
    try {
      if (!idFeedProduct) {
        await FeedProductService.store({ ...data, agolineContent: data.agolineContent?.toString()?.replace(',', '.') });
        notify(t('Successfully created feed product'), 'success');
      } else {
        await FeedProductService.update(idFeedProduct, { ...data, agolineContent: data.agolineContent?.toString()?.replace(',', '.') });
        notify(t('Successfully updated feed product'), 'success');
      }
      fetchData();
      handleHiddenDrawer();
    } catch (error) {
      handleErrors(error);
    }
    setIsLoading(false);
  }

  function errorTab() {
    //@ts-ignore
    return formState.errors?.languages?.map((lang, index) => index)?.filter(item => typeof (item) === 'number');
  }

  function getMenuItem(label: string, index: number) {
    const errors = errorTab() || [];

    return (
      <StyledMenuItem key={label} className='tab-item'>
        <div className={`${errors.includes(index) ? 'error' : ''}`}>{label}</div>
      </StyledMenuItem>
    );
  }

  function renderLanguageFields() {
    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        renderActiveOnly={false}
        panes={languages.map((language) => {
          const indexLanguage = languages.findIndex(lang => lang.id === language.id);
          return {
            //@ts-ignore
            menuItem: getMenuItem(language.label, indexLanguage),
            pane: (
              <StyledTabPane key={language.id}>
                <FormFields.Input
                  control={control}
                  name={`languages[${indexLanguage}].name`}
                  label={t('Designation')}
                  placeholder={t('Designation')}
                  rules={{
                    required: {
                      value: true,
                      message: t('Please enter feed product name')
                    }
                  }}
                />
              </StyledTabPane>
            )
          };
        })}
      />
    );
  }

  function renderStatusField() {
    return (
      <Checkbox
        label={t('Active')}
        checked={Boolean(watchStatus)}
        onChange={(_, data) => setValue('status', data.checked ? 1 : 0)}
        style={{ marginBottom: '1em' }}
      />
    );
  }

  function renderFeedSupplierField() {
    return (
      <FormFields.Select
        control={control}
        name='feedSupplierId'
        label={t('Feed supplier')}
        placeholder={t('Select feed supplier')}
        options={feedSupplierOptions}
        rules={{
          required: {
            value: true,
            message: t('Please select a feed supplier')
          }
        }}
      />
    );
  }

  function renderAgolineContentField() {
    return (
      <FormFields.Input
        control={control}
        name='agolineContent'
        label={t('Agoline content')}
        typeInput='controled'
        decimalScale={2}
        suffix=' %'
        allowDecimals
        placeholder={t('Enter agoline content')}
        type='number'
        rules={{
          required: {
            value: true,
            message: t('Please enter agoline content')
          },
          validate: (value) => {
            const transformValue = typeof (value) === 'string' ? parseFloat(value?.replace(',', '.')) : value
            if (transformValue > 100) {
              return t('Max percent value is 100%')
            }
            return true
          }
        }}
      />
    );
  }

  const renderContent = () => {
    return (
      <>
        {renderStatusField()}
        {renderFeedSupplierField()}
        {renderAgolineContentField()}
        {renderLanguageFields()}
        <DrawerFooter>
          <GlobalEl.PrimaryButton
          >
            {idFeedProduct ? t('Update') : t('Add')}
          </GlobalEl.PrimaryButton>
        </DrawerFooter>
      </>
    );
  };

  return (
    <Drawer
      isVisible={isVisible}
      handleSubmit={handleSubmit(onSubmit)}
      isBusy={isLoading || isLoadingSuppliers}
      header={idFeedProduct ? t('Edit feed product') : t('Create feed product')}
      closeOnEscape
      onHide={() => {
        reset();
        handleHiddenDrawer();
      }}
      content={renderContent()}
    />
  );
};

export default CreateEditFeedProduct;
